/**
 * Global bridge for surfacing quota-exceeded (HTTP 429) responses to the UI.
 *
 * The axios error interceptor cannot render React, so it dispatches a window
 * CustomEvent that the <UpgradePromptProvider> listens for and turns into a modal.
 */
export const QUOTA_EXCEEDED_EVENT = "omc:quota-exceeded";

export interface QuotaExceededDetail {
  /** "daily_quota_exceeded" | "monthly_quota_exceeded" | normalized code */
  code: string;
  message: string;
}

const QUOTA_CODES = new Set([
  "daily_quota_exceeded",
  "monthly_quota_exceeded",
  "DAILY_QUOTA_EXCEEDED",
  "MONTHLY_QUOTA_EXCEEDED",
  "CHAT_MESSAGE_QUOTA_EXCEEDED",
]);

/** Extract a quota-exceeded payload from an axios 429 error, or null if not a quota error. */
export function extractQuotaError(error: unknown): QuotaExceededDetail | null {
  const err = error as {
    response?: { status?: number; data?: { detail?: unknown } };
  };
  if (err?.response?.status !== 429) return null;

  const detail = err.response?.data?.detail;
  let rawCode: string | undefined;
  let message: string | undefined;

  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const d = detail as Record<string, unknown>;
    rawCode =
      (typeof d.code === "string" && d.code) ||
      (typeof d.error === "string" && d.error) ||
      undefined;
    message = typeof d.message === "string" ? d.message : undefined;
  } else if (typeof detail === "string") {
    message = detail;
  }

  const isQuota =
    (rawCode && QUOTA_CODES.has(rawCode)) ||
    (rawCode ? /quota/i.test(rawCode) : false) ||
    (message ? /quota|limit/i.test(message) : false);

  if (!isQuota) return null;

  return {
    code: rawCode ?? "quota_exceeded",
    message: message ?? "You've reached your usage limit for now.",
  };
}

export function emitQuotaExceeded(detail: QuotaExceededDetail): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(QUOTA_EXCEEDED_EVENT, { detail }));
}
